import React, { memo, useCallback, useState } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
  Box,
  Button,
  Checkbox,
  EntitySelector,
  EntitySelectorContent,
  EntitySelectorEmpty,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorTrigger,
  Icon,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Label,
  ScrollArea,
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
  Separator,
  Text,
  Textarea,
  toastManager,
  type EntitySelection,
  DND_DRAGGABLE_PROPS,
} from "@salestrak/ui";

import { CURRENCY_OPTIONS } from "@salestrak/custom-fields";
import { getFieldTypeMeta } from "#app/lib/constants/field-type-icons";
import { useFieldTypeSelectorConfig } from "#app/lib/hooks/use-field-type-order";
import { FIELD_RENDERERS } from "#app/components/custom-fields/custom-field-renderers";
import type { CustomFieldDef } from "#app/components/custom-fields/types";
import type {
  CreateCustomFieldInput,
  CustomFieldConfigInput,
  CustomFieldOptionInput,
  CustomFieldRow,
  CustomFieldType,
  UpdateCustomFieldInput,
} from "#app/lib/data";
import type { Omittable } from "@salestrak/ui";
import { at } from "#app/lib/utils/at";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type CustomFieldFormMode =
  | { mode: "create"; type: CustomFieldType }
  | { mode: "edit"; field: CustomFieldRow };

interface CustomFieldFormDialogProps {
  formMode: CustomFieldFormMode | null;
  onClose: () => void;
  onCreate: (input: CreateCustomFieldInput) => Promise<unknown>;
  onUpdate: (id: string, input: UpdateCustomFieldInput) => Promise<unknown>;
  onDelete: (field: CustomFieldRow) => void;
}

// ---------------------------------------------------------------------------
// Select option helpers
// ---------------------------------------------------------------------------

type OptionRow = Omittable<{
  id: string;
  label: string;
  // The persisted option value. Preserved across label edits so renaming an
  // option does not orphan stored issue values (which reference this value,
  // not the label). Undefined for newly-added options, whose value is derived
  // from the label at save time.
  value?: string;
}>;

let optionIdCounter = 0;
function makeEmptyOption(): OptionRow {
  return { id: `opt-${++optionIdCounter}`, label: "" };
}

// ---------------------------------------------------------------------------
// Currency options (module-level, computed once)
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// Field type categories
// ---------------------------------------------------------------------------

const SELECT_TYPES = new Set<CustomFieldType>(["select"]);

// Hover-revealed row affordances: CSS, not a hover state hook.
const HOVER_REVEAL =
  "opacity-0 transition-opacity group-hover/option:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100";

// Every type accepts an optional default value except member — its candidate
// list is workspace data, not authored on the field.
// member's candidate list is workspace data, and photos are per-issue
// uploads — neither authors a default here.
const NO_DEFAULT_TYPES = new Set<CustomFieldType>(["member", "photos"]);

const MAX_OPTION_LABEL_LENGTH = 255;

// A default value is "empty" (omit from config) when blank for its type.
// Checkbox is special: `false` is a real default, only `null` is empty.
function isDefaultValueEmpty(type: CustomFieldType, value: unknown): boolean {
  if (value == null) return true;
  if (type === "checkbox") return false;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  if (type === "address") return Object.keys(value as object).length === 0;
  return false;
}

// Filter a select default (an array of option values) down to values still
// present in the option list. Returns the input unchanged for non-select types.
function pruneSelectDefault(
  type: CustomFieldType,
  value: unknown,
  optionValues: Set<string> | null,
): unknown {
  if (!optionValues) return value;
  if (type === "select" && Array.isArray(value)) {
    return value.filter((v) => typeof v === "string" && optionValues.has(v));
  }
  return value;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CustomFieldFormDialog({
  formMode,
  onClose,
  onCreate,
  onUpdate,
  onDelete,
}: CustomFieldFormDialogProps) {
  if (!formMode) return null;

  return (
    <CustomFieldFormContent
      key={formMode.mode === "create" ? `create-${formMode.type}` : `edit-${formMode.field.id}`}
      formMode={formMode}
      onClose={onClose}
      onCreate={onCreate}
      onUpdate={onUpdate}
      onDelete={onDelete}
    />
  );
}

// ---------------------------------------------------------------------------
// Inner form (remounts via key — no useEffect reset needed)
// ---------------------------------------------------------------------------

const CREATE_DEFAULTS = {
  name: "",
  description: "",
  currency: "usd",
  defaultValue: null as unknown,
  multiple: true,
  includeTeams: false,
};

function initFormState(formMode: CustomFieldFormMode) {
  if (formMode.mode === "create") {
    return { ...CREATE_DEFAULTS, options: [makeEmptyOption()] };
  }
  const f = formMode.field;
  const existingOpts = f.config?.options;
  return {
    name: f.name,
    description: f.description ?? "",
    currency: f.config?.currency?.toLowerCase() ?? "usd",
    defaultValue: f.config?.defaultValue ?? null,
    multiple: f.config?.multiple !== false,
    includeTeams: f.config?.includeTeams === true,
    options:
      existingOpts && existingOpts.length > 0
        ? existingOpts.map((o) => ({
            id: `opt-${++optionIdCounter}`,
            label: o.label ?? "",
            value: o.value ?? undefined,
          }))
        : [makeEmptyOption()],
  };
}

interface CustomFieldFormContentProps {
  formMode: CustomFieldFormMode;
  onClose: () => void;
  onCreate: (input: CreateCustomFieldInput) => Promise<unknown>;
  onUpdate: (id: string, input: UpdateCustomFieldInput) => Promise<unknown>;
  onDelete: (field: CustomFieldRow) => void;
}

// ---------------------------------------------------------------------------
// Sortable choice row
// ---------------------------------------------------------------------------

const SortableChoiceRow = memo(function SortableChoiceRow({
  opt,
  index,
  total,
  onLabelChange,
  onRemove,
}: {
  opt: OptionRow;
  index: number;
  total: number;
  onLabelChange: (index: number, val: string) => void;
  onRemove: (index: number) => void;
}) {
  const {
    attributes: { role: _role, ...attributes },
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isSorting,
  } = useSortable({ id: opt.id });

  return (
    // Box carries no motion props, so the dnd-kit transform/transition and the
    // drag opacity are inline style + a class. The hover reveal is a group
    // variant in CSS, not a hover state hook.
    <Box
      ref={(node) => setNodeRef(node as unknown as HTMLElement)}
      style={{
        transform: CSS.Transform.toString(transform),
        transition: isSorting ? (transition ?? undefined) : undefined,
      }}
      className={isDragging ? "group/option opacity-30" : "group/option"}
      position="relative"
      fill="width"
    >
      <InputGroup>
        <InputGroupInput
          value={opt.label}
          onChange={(event) => onLabelChange(index, event.target.value)}
          placeholder={`Option ${index + 1}`}
        />
        {total > 1 ? (
          <InputGroupAddon align="inline-end">
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label="Remove option"
              className={HOVER_REVEAL}
              onClick={() => onRemove(index)}
            >
              <Icon name="x" size="xs" color="muted" />
            </Button>
          </InputGroupAddon>
        ) : null}
      </InputGroup>
      {/* Drag handle overlays the input's leading edge — no flow space. */}
      {total > 1 ? (
        <Box
          position="absolute"
          left={-16}
          top={0}
          bottom={0}
          justify="center"
          align="start"
          paddingRight="md"
          cursor="grab"
          className={HOVER_REVEAL}
          {...DND_DRAGGABLE_PROPS}
          {...(listeners as any)}
          {...(attributes as any)}
        >
          <Icon name="grip-vertical" size="sm" color="muted" />
        </Box>
      ) : null}
    </Box>
  );
});

// ---------------------------------------------------------------------------
// Default value editor — reuses the live field renderer for the chosen type
// so each type gets its native editor (phone country picker, date picker,
// select dropdown, checkbox, etc.) for free.
// ---------------------------------------------------------------------------

function DefaultValueEditor({
  fieldType,
  currency,
  options,
  multiple,
  value,
  onChangeValue,
}: {
  fieldType: CustomFieldType;
  currency: string;
  options: OptionRow[];
  multiple: boolean;
  value: unknown;
  onChangeValue: (value: unknown) => void;
}) {
  const Renderer = FIELD_RENDERERS[fieldType];
  if (!Renderer) return null;

  // Synthesize a field def from the in-progress form so the renderer sees
  // the same config the saved field will carry — including `multiple`, so a
  // multi-select field's default editor allows picking more than one value.
  const field: CustomFieldDef = {
    __typename: "CustomField",
    id: "__default__",
    customFieldId: "__default__",
    name: "Default value",
    key: "__default__",
    description: null,
    type: fieldType,
    config: {
      __typename: "CustomFieldConfigView",
      min: null,
      max: null,
      step: null,
      defaultValue: null,
      multiline: null,
      includeTeams: null,
      currency: fieldType === "money" ? currency.toUpperCase() : null,
      multiple: SELECT_TYPES.has(fieldType) ? multiple : null,
      options: SELECT_TYPES.has(fieldType)
        ? options
            .filter((o) => o.label.trim())
            .map((o) => ({ __typename: "CustomFieldOptionView" as const, label: o.label.trim(), value: o.value ?? o.label.trim() }))
        : null,
    },
  };

  // No `isActive`: this is a passive form control, so the select/date
  // renderers must not auto-open their dropdown/picker on mount.
  return (
    <Renderer
      field={field}
      value={value}
      onChangeValue={onChangeValue}
      surface="default"
    />
  );
}

function CustomFieldFormContent({
  formMode,
  onClose,
  onCreate,
  onUpdate,
  onDelete,
}: CustomFieldFormContentProps) {
  const isCreate = formMode.mode === "create";
  const [fieldType, setFieldType] = useState<CustomFieldType>(
    formMode.mode === "create"
      ? formMode.type
      : (formMode.field.type as CustomFieldType),
  );

  const typeMeta = getFieldTypeMeta(fieldType);
  const fieldTypeSelectorConfig = useFieldTypeSelectorConfig();

  // --- Form state (initialized from formMode, resets on remount via key) ---
  const [formInit] = useState(() => initFormState(formMode));
  const [name, setName] = useState(formInit.name);
  const [description, setDescription] = useState(formInit.description);
  const [defaultValue, setDefaultValue] = useState<unknown>(formInit.defaultValue);
  const [currency, setCurrency] = useState(formInit.currency);
  const [multiple, setMultiple] = useState(formInit.multiple);
  const [includeTeams, setIncludeTeams] = useState(formInit.includeTeams);
  const [options, setOptions] = useState<OptionRow[]>(formInit.options);
  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [optionsError, setOptionsError] = useState<string | null>(null);

  // --- Option management ---
  const handleOptionLabelChange = useCallback(
    (index: number, label: string) => {
      setOptions((prev) => {
        const next = [...prev];
        next[index] = { ...at(prev, index, "option"), label };
        return next;
      });
    },
    [],
  );

  const handleAddOption = useCallback(() => {
    setOptions((prev) => [...prev, makeEmptyOption()]);
  }, []);

  const handleRemoveOption = useCallback((index: number) => {
    setOptions((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  // --- Drag and drop ---
  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setOptions((prev) => {
        const oldIndex = prev.findIndex((o) => o.id === active.id);
        const newIndex = prev.findIndex((o) => o.id === over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  }, []);

  // --- Validation ---
  const validate = useCallback((): string | null => {
    if (!name.trim()) return "Name is required";
    if (SELECT_TYPES.has(fieldType)) {
      const validOptions = options.filter((o) => o.label.trim());
      if (validOptions.length === 0) return "At least one option is required";
      const labels = validOptions.map((o) => o.label.trim().toLowerCase());
      if (new Set(labels).size !== labels.length)
        return "Option labels must be unique";
      if (validOptions.some((o) => o.label.trim().length > MAX_OPTION_LABEL_LENGTH))
        return `Option labels must be ${MAX_OPTION_LABEL_LENGTH} characters or fewer`;
    }
    return null;
  }, [name, fieldType, options]);

  const showsChoicesSection = SELECT_TYPES.has(fieldType);
  const showsDangerZone = !isCreate;

  // --- Build config ---
  const buildConfig = useCallback((): CustomFieldConfigInput | undefined => {
    const config: CustomFieldConfigInput = {};
    let hasConfig = false;

    if (fieldType === "money" && currency) {
      config.currency = currency.toUpperCase();
      hasConfig = true;
    }

    let optionValues: Set<string> | null = null;
    if (SELECT_TYPES.has(fieldType)) {
      const validOptions: CustomFieldOptionInput[] = options
        .filter((o) => o.label.trim())
        // Preserve an existing option's persisted value; only derive a fresh
        // value from the label for newly-added options. Renaming a saved
        // option keeps its value stable so stored issue values still resolve.
        .map((o) => ({ label: o.label.trim(), value: o.value ?? o.label.trim() }));
      if (validOptions.length > 0) {
        config.options = validOptions;
        hasConfig = true;
      }
      optionValues = new Set(validOptions.map((o) => o.value));
    }

    if (fieldType === "member") {
      config.multiple = multiple;
      config.includeTeams = includeTeams;
      hasConfig = true;
    } else if (fieldType === "select" || fieldType === "photos") {
      config.multiple = multiple;
      hasConfig = true;
    }

    if (fieldType === "member") {
      // member never carries an authored default
    } else if (!isDefaultValueEmpty(fieldType, defaultValue)) {
      // member fields don't author a default; every other type can. For
      // selects, drop a default no longer backed by an option so the API
      // doesn't reject it.
      const pruned = pruneSelectDefault(fieldType, defaultValue, optionValues);
      if (!isDefaultValueEmpty(fieldType, pruned)) {
        // defaultValue is the GraphQL Object scalar (any shape per type).
        config.defaultValue = pruned as CustomFieldConfigInput["defaultValue"];
        hasConfig = true;
      }
    }

    return hasConfig ? config : undefined;
  }, [fieldType, currency, options, multiple, includeTeams, defaultValue]);

  // --- Save ---
  const handleSave = useCallback(async () => {
    // Clear previous errors
    setNameError(null);
    setOptionsError(null);

    const error = validate();
    if (error) {
      // Set inline errors on the relevant fields
      if (error === "Name is required") {
        setNameError(error);
      } else {
        setOptionsError(error);
      }
      toastManager.add({ title: error, type: "error" });
      return;
    }

    setSaving(true);
    try {
      if (formMode.mode === "create") {
        const input: CreateCustomFieldInput = {
          name: name.trim(),
          type: fieldType,
          description: description.trim() || undefined,
          config: buildConfig(),
        };
        await onCreate(input);
      } else {
        const input: UpdateCustomFieldInput = {
          name: name.trim(),
          description: description.trim() || undefined,
          config: buildConfig(),
        };
        await onUpdate(formMode.field.id, input);
      }
      onClose();
    } catch {
      // Errors are toasted by the mutation hook
    } finally {
      setSaving(false);
    }
  }, [formMode, fieldType, name, description, validate, buildConfig, onCreate, onUpdate, onClose]);

  return (
    <Box direction="column" fill="flex">
      <Separator />
      <Box paddingLeft="lg" paddingTop="md" paddingBottom="md" paddingRight="md" direction="row" align="center" justify="between">
        <Text size="sm" weight="semibold">
          {isCreate ? "Create field" : "Edit field"}
        </Text>
        <Button variant="ghost" size="icon-sm" aria-label="Close panel" onClick={onClose}>
          <Icon name="panel-right-close" size="sm" color="muted" />
        </Button>
      </Box>

      <Separator />

      {/* ScrollArea owns its scrollbar chrome — there is nothing to tune from
          this call site. */}
      <ScrollArea>
        <Box paddingX="lg" paddingTop="md" paddingBottom="md">
          <Accordion multiple defaultValue={["general"]}>
            <AccordionItem value="general" className="border-b-0">
              <AccordionTrigger className="py-0">
                <Text size="sm" weight="semibold">General</Text>
              </AccordionTrigger>
              <AccordionPanel>
                <Box paddingTop="sm" paddingBottom="md">
                  <Box gap="md">
                    <Box gap="xs">
                      <Text size="sm" weight="medium" variant="muted">Type</Text>
                      {isCreate ? (
                        <EntitySelector
                          config={fieldTypeSelectorConfig}
                          value={[]}
                          onValueChange={(selections: EntitySelection[]) => {
                            const selected = selections[0];
                            if (selected) {
                              setFieldType(selected.id as CustomFieldType);
                              setOptions([makeEmptyOption()]);
                              setCurrency(CREATE_DEFAULTS.currency);
                              setDefaultValue(CREATE_DEFAULTS.defaultValue);
                              setMultiple(CREATE_DEFAULTS.multiple);
                              setIncludeTeams(CREATE_DEFAULTS.includeTeams);
                            }
                          }}
                        >
                          <EntitySelectorTrigger
                            render={
                              <Button variant="outline" className="w-full">
                                <Icon
                                  name={typeMeta.icon}
                                  size="sm"
                                  color={typeMeta.color}
                                />
                                <Text>{typeMeta.label}</Text>
                                <Box grow />
                                <Icon name="chevron-down" size="xs" color="muted" />
                              </Button>
                            }
                          />
                          <EntitySelectorContent size="sm">
                            <EntitySelectorInput />
                            <EntitySelectorPool value="field-types" />
                            <EntitySelectorEmpty>No matching field types</EntitySelectorEmpty>
                          </EntitySelectorContent>
                        </EntitySelector>
                      ) : (
                        <Box direction="row" align="center" gap="sm">
                          <Icon name={typeMeta.icon} size="sm" color={typeMeta.color} />
                          <Text size="sm" variant="muted">{typeMeta.label}</Text>
                        </Box>
                      )}
                    </Box>

                    <Box gap="xs">
                      <Text size="sm" weight="medium" variant="muted">Name</Text>
                      <Input
                        value={name}
                        onChange={(event) => {
                          setName(event.target.value);
                          if (nameError) setNameError(null);
                        }}
                        placeholder="Field name"
                        autoFocus
                        aria-invalid={!!nameError}
                      />
                      {nameError ? (
                        <Text size="xs" variant="destructive">{nameError}</Text>
                      ) : null}
                    </Box>

                    <Box gap="xs">
                      <Box direction="row" justify="between" align="end">
                        <Text size="sm" weight="medium" variant="muted">Description</Text>
                        <Text size="xs" variant="muted">Optional</Text>
                      </Box>
                      <Textarea
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                        placeholder="Optional description (shown as tooltip)"
                        rows={2}
                      />
                    </Box>

                    {fieldType === "money" ? (
                      <Box gap="xs">
                        <Text size="sm" weight="medium" variant="muted">Currency</Text>
                        <Select
                          items={CURRENCY_OPTIONS}
                          value={currency ?? null}
                          onValueChange={(next: string | null) => { if (next) setCurrency(next); }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select currency..." />
                          </SelectTrigger>
                          <SelectPopup>
                            {CURRENCY_OPTIONS.map((c) => (
                              <SelectItem key={c.value} value={c.value}>
                                {c.label}
                              </SelectItem>
                            ))}
                          </SelectPopup>
                        </Select>
                      </Box>
                    ) : null}

                    {/* member config — cardinality + team visibility */}
                    {fieldType === "member" ? (
                      <Box gap="sm">
                        {/* Checkbox has no label slot; a `Label` wraps both
                            parts instead. */}
                        <Label>
                          <Checkbox checked={multiple} onCheckedChange={setMultiple} />
                          <Text size="sm" weight="normal">
                            {includeTeams ? "Allow multiple members/teams" : "Allow multiple members"}
                          </Text>
                        </Label>
                        <Label>
                          <Checkbox
                            checked={includeTeams}
                            onCheckedChange={setIncludeTeams}
                          />
                          <Text size="sm" weight="normal">Include teams</Text>
                        </Label>
                      </Box>
                    ) : null}

                    {/* photos config — cardinality */}
                    {fieldType === "photos" ? (
                      <Label>
                        <Checkbox checked={multiple} onCheckedChange={setMultiple} />
                        <Text size="sm" weight="normal">Allow multiple photos</Text>
                      </Label>
                    ) : null}

                    {/* Default value — every type except member (its
                        candidate list is workspace data, not authored here). */}
                    {!NO_DEFAULT_TYPES.has(fieldType) ? (
                      <Box gap="xs">
                        <Box direction="row" justify="between" align="end">
                          <Text size="sm" weight="medium" variant="muted">Default value</Text>
                          <Text size="xs" variant="muted">Optional</Text>
                        </Box>
                        <DefaultValueEditor
                          fieldType={fieldType}
                          currency={currency}
                          options={options}
                          multiple={multiple}
                          value={defaultValue}
                          onChangeValue={setDefaultValue}
                        />
                      </Box>
                    ) : null}
                  </Box>
                </Box>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </Box>
        {!showsChoicesSection && !showsDangerZone ? <Separator /> : null}

        {/* Choices — select types */}
        {showsChoicesSection ? (
          <>
            <Separator />
            <Box paddingX="lg" paddingTop="md" paddingBottom="md">
              <Accordion multiple defaultValue={["choices"]}>
                <AccordionItem value="choices" className="border-b-0">
                <AccordionTrigger className="py-0">
                  <Text size="sm" weight="semibold">Choices</Text>
                </AccordionTrigger>
                <AccordionPanel>
                  <Box paddingTop="sm" paddingBottom="md">
                    <Box gap="md">
                      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={options.map((o) => o.id)} strategy={verticalListSortingStrategy}>
                          <Box gap="sm">
                            {options.map((opt, i) => (
                              <SortableChoiceRow
                                key={opt.id}
                                opt={opt}
                                index={i}
                                total={options.length}
                                onLabelChange={handleOptionLabelChange}
                                onRemove={handleRemoveOption}
                              />
                            ))}
                          </Box>
                        </SortableContext>
                      </DndContext>
                      <Box direction="row" justify="end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleAddOption}
                        >
                          <Icon name="plus" size="sm" color="muted" />
                          <Text>Add option</Text>
                        </Button>
                      </Box>

                      <Label>
                        <Checkbox
                          checked={multiple}
                          onCheckedChange={(checked) => {
                            setMultiple(checked);
                            // Single→multi keeps the array; multi→single caps at one.
                            setDefaultValue((prev: unknown) =>
                              checked || !Array.isArray(prev)
                                ? prev
                                : prev.slice(0, 1),
                            );
                          }}
                        />
                        <Text size="sm" weight="normal">Allow multiple selections</Text>
                      </Label>
                    </Box>
                  </Box>
                </AccordionPanel>
              </AccordionItem>
              </Accordion>
            </Box>
            {!showsDangerZone ? <Separator /> : null}
          </>
        ) : null}

        {!isCreate ? (
          <>
            <Separator />
            <Box paddingX="lg" paddingTop="md" paddingBottom="md">
              <Accordion multiple defaultValue={["danger-zone"]}>
                <AccordionItem value="danger-zone" className="border-b-0">
                  <AccordionTrigger className="py-0">
                    <Text size="sm" weight="semibold">Danger Zone</Text>
                  </AccordionTrigger>
                  <AccordionPanel>
                    <Box paddingTop="sm">
                      <Box direction="row" align="center" justify="between">
                        <Text size="sm" weight="medium" variant="muted">Delete custom field</Text>
                        <Button
                          variant="destructive-outline"
                          size="sm"
                          onClick={() => onDelete(formMode.field)}
                        >
                          <Icon name="trash-2" size="sm" />
                          <Text>Delete field...</Text>
                        </Button>
                      </Box>
                    </Box>
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            </Box>
            <Separator />
          </>
        ) : null}
      </ScrollArea>

      <Box>
        <Separator />
        <Box padding="lg">
          <Box direction="row" gap="sm" justify="end">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              <Text>Cancel</Text>
            </Button>
            <Button onClick={handleSave} disabled={saving} loading={saving}>
              <Text>{isCreate ? "Create field" : "Save changes"}</Text>
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
